export type CostRow = Record<string, unknown>
export interface CostVersion extends CostRow {
  id: number; product_id: number; version_number: number; status: string; row_version: number
  costing_currency_code: string; lines?: CostRow[]; totals?: CostRow[]; prices?: CostRow[]
}
export const draftFields = ['product_id','bom_version_id','costing_currency_id','production_type','costing_date','effective_from','effective_to','batch_quantity','expected_output_quantity','yield_rate','cost_type','shift','price_resolution_policy','scenario_name','notes'] as const
export function newDraft(version?: CostVersion | null): Record<string,string> {
  const now=new Date()
  const date=[now.getFullYear(),String(now.getMonth()+1).padStart(2,'0'),String(now.getDate()).padStart(2,'0')].join('-')
  const defaults:Record<string,string>={product_id:'',bom_version_id:'',costing_currency_id:'',production_type:'socks',costing_date:date,effective_from:'',effective_to:'',batch_quantity:'1',expected_output_quantity:'1',yield_rate:'1',cost_type:'standard',shift:'day',price_resolution_policy:'approved_purchase_price',scenario_name:'',notes:''}
  for(const field of draftFields) if(version?.[field]!=null) defaults[field]=String(version[field])
  return defaults
}
export function draftPayload(draft:Record<string,string>): Record<string,string | null> {
  return Object.fromEntries(draftFields.map(field=>[field, ['effective_from','effective_to','scenario_name','notes'].includes(field) ? draft[field] || null : draft[field]]))
}
function metadata(value:unknown):CostRow {
  if(typeof value==='string') { try { const result=JSON.parse(value); return result && typeof result==='object' ? result : {} } catch { return {} } }
  return value && typeof value==='object' ? value as CostRow : {}
}
function lineKey(line:CostRow):string {
  const info=metadata(line.resolution_metadata)
  // Price record IDs can change between calculations; prefer stable BOM identity.
  const identity=info.bom_material_id!=null ? ['material',info.bom_material_id] :
    info.bom_operation_id!=null ? ['operation',info.bom_operation_id] :
    ['source',line.source_type,line.source_id,line.name]
  return JSON.stringify([line.cost_element,...identity])
}
export function compareLines(left:CostRow[],right:CostRow[]) {
  const indexed=new Map<string, {key:string;left?:CostRow;right?:CostRow}>()
  for(const [side,lines] of [['left',left],['right',right]] as const) {
    const occurrences=new Map<string,number>()
    for(const line of lines) {
      const base=lineKey(line); const count=occurrences.get(base)??0; occurrences.set(base,count+1)
      const key=base+':'+count
      const row=indexed.get(key)??{key}; row[side]=line; indexed.set(key,row)
    }
  }
  return [...indexed.values()]
}
export function comparable(left:CostVersion,right:CostVersion):boolean {
  return String(left.product_id)===String(right.product_id) &&
    String(left.costing_currency_id)===String(right.costing_currency_id) &&
    left.production_type===right.production_type && left.shift===right.shift &&
    left.cost_type===right.cost_type
}

const KNITTING_OPERATION = /(knit|to[’'`‘]?qish|вяза|трикот)/i

/**
 * Operation sequences that represent the knitting step.
 *
 * The costing API labels material lines from the material's product group only, so a
 * sweater/yarn group that does not contain "knit", "yarn" or "thread" is reported as
 * `direct_material_other`. Socks routes always start with knitting, so the first
 * operation is used when no operation name identifies the step explicitly.
 */
export function knittingOperationSequences(operations:CostRow[],materials:CostRow[]):Set<number> {
  const sequences=new Set<number>()
  for(const operation of operations) {
    const name=String(operation.name ?? operation.operation_name ?? '')
    if(KNITTING_OPERATION.test(name)) sequences.add(Number(operation.operation_sequence))
  }
  if(sequences.size) return sequences
  const all=[...operations,...materials]
    .map(row=>Number(row.operation_sequence))
    .filter(sequence=>Number.isFinite(sequence))
  if(all.length) sequences.add(Math.min(...all))
  return sequences
}

export function isKnittingMaterial(material:CostRow,sequences:Set<number>):boolean {
  return sequences.has(Number(material.operation_sequence))
}

/** Maps a calculated line to the reference tab, using its BOM material when the API code is generic. */
export function displayCostElement(line:CostRow,materials:Map<string,CostRow>,sequences:Set<number>):string {
  const element=String(line.cost_element ?? '')
  if(element!=='direct_material_other') return element
  const info=metadata(line.resolution_metadata)
  const material=materials.get(String(info.bom_material_id ?? ''))
  return material && isKnittingMaterial(material,sequences) ? 'direct_material_knitting' : element
}
